import { useState } from 'react';
import { Languages, Loader2 } from 'lucide-react';
import clsx from 'clsx';
import { apiClient } from '@/api/client';

interface VoiceTranscriptProps {
  messageId: string;
  transcription: string | null;
  isOwn: boolean;
  /** Called with the freshly received transcription so the parent can cache it. */
  onUpdate: (text: string) => void;
}

/**
 * On-demand voice transcription block. Shows a "Перевести в текст" button
 * until the user clicks it; then a spinner while Whisper works; then the
 * recognized text in italic.
 *
 * Server endpoint: POST /api/messages/:id/transcribe — idempotent (returns
 * cached transcription if already done).
 */
export default function VoiceTranscript({ messageId, transcription, isOwn, onUpdate }: VoiceTranscriptProps) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Local fallback when the parent doesn't have a store path to persist the
  // transcript (e.g. DM messages mapped through dmToMessage).
  const [local, setLocal] = useState<string | null>(null);
  const text = transcription ?? local;

  if (text) {
    return (
      <p className={clsx(
        'text-xs italic leading-snug break-words whitespace-pre-wrap pt-1 border-t border-white/10',
        isOwn ? 'text-white/85' : 'text-surface-300',
      )}>
        {text}
      </p>
    );
  }

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { data } = await apiClient.post<{ transcription: string }>(
        `/api/messages/${messageId}/transcribe`,
        undefined,
        { timeout: 120_000 }, // Whisper-tiny ~10–60 s on cold/medium audio
      );
      if (data.transcription) {
        setLocal(data.transcription);
        onUpdate(data.transcription);
      }
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg ?? 'Не удалось распознать');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pt-1 border-t border-white/10">
      <button
        onClick={handleClick}
        disabled={busy}
        className={clsx(
          'flex items-center gap-1.5 text-xs px-1 py-0.5 rounded transition-colors',
          isOwn ? 'text-white/70 hover:text-white' : 'text-surface-400 hover:text-surface-200',
          busy && 'opacity-60 cursor-wait',
        )}
      >
        {busy ? <Loader2 size={11} className="animate-spin" /> : <Languages size={11} />}
        <span>{busy ? 'Распознаю…' : 'Перевести в текст'}</span>
      </button>
      {error && <p className="text-xs text-red-300 mt-0.5">{error}</p>}
    </div>
  );
}
